import {
  UAE_ADMIN_FEE_RENTAL_AED,
  UAE_ADMIN_FEE_SALE_AED,
  UAE_EXCLUSIVE_AGENT_PERCENT,
  UAE_FASTPAY_DEDUCTION_POINTS,
  UAE_TYPICAL_MARKET_PERCENT,
  UAE_MODEL_DEFAULTS,
  UAE_MODEL_LIMITS,
  UAE_SERVICE_FEE_INSTALMENT_AED,
  UAE_SERVICE_FEE_INSTALMENTS,
  type UaeModel,
} from '~/constants/uaeModel';

export type UaeModelInput = { [K in keyof UaeModel]?: unknown };

const toFiniteNumber = (raw: unknown): number | null => {
  if (raw === null || raw === undefined || raw === '' || typeof raw === 'boolean') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const percentOr = (raw: unknown, fallback: number): number => {
  const n = toFiniteNumber(raw);
  return n === null ? fallback : clamp(Math.round(n), UAE_MODEL_LIMITS.percentMin, UAE_MODEL_LIMITS.percentMax);
};

/** Any partial/untrusted input → a valid UAE model; invalid or missing fields fall back to the defaults. */
export const normalizeUaeModel = (input: UaeModelInput | null | undefined): UaeModel => {
  const src = input ?? {};
  const cap = toFiniteNumber(src.capAed);
  const months = toFiniteNumber(src.cycleMonths);
  const before = percentOr(src.agentPercentBeforeCap, UAE_MODEL_DEFAULTS.agentPercentBeforeCap);
  const after = percentOr(src.agentPercentAfterCap, UAE_MODEL_DEFAULTS.agentPercentAfterCap);
  return {
    agentPercentBeforeCap: before,
    // The split never goes down after the cap.
    agentPercentAfterCap: Math.max(before, after),
    capAed: cap !== null && cap > 0 ? Math.min(Math.round(cap), UAE_MODEL_LIMITS.capMaxAed) : UAE_MODEL_DEFAULTS.capAed,
    cycleMonths:
      months !== null && months >= UAE_MODEL_LIMITS.cycleMonthsMin
        ? Math.min(Math.round(months), UAE_MODEL_LIMITS.cycleMonthsMax)
        : UAE_MODEL_DEFAULTS.cycleMonths,
    distressClaimsExtended: src.distressClaimsExtended === true,
  };
};

/** "AED 400K" / "AED 1.5M" — compact cap label used in headings and FAQ copy. */
export const formatAedCompact = (value: number): string => {
  const n = Math.round(value);
  if (n >= 1_000_000) return `AED ${Number((n / 1_000_000).toFixed(2))}M`;
  if (n >= 1_000) return `AED ${Number((n / 1_000).toFixed(1))}K`;
  return `AED ${n}`;
};

/** i18n interpolation params shared by every /uae string that mentions the model. */
export const uaeModelCopyParams = (model: UaeModel) => ({
  before: `${model.agentPercentBeforeCap}%`,
  after: `${model.agentPercentAfterCap}%`,
  cap: formatAedCompact(model.capAed),
  capFull: `AED ${Math.round(model.capAed).toLocaleString('en-US')}`,
  platformCut: `${100 - model.agentPercentBeforeCap}%`,
  // FastPay on any deal: percentage points off the split (owner answers 5 and 25).
  beforeFastPay: `${Math.max(0, model.agentPercentBeforeCap - UAE_FASTPAY_DEDUCTION_POINTS)}%`,
  afterFastPay: `${Math.max(0, model.agentPercentAfterCap - UAE_FASTPAY_DEDUCTION_POINTS)}%`,
  months: String(model.cycleMonths),
  typical: `${UAE_TYPICAL_MARKET_PERCENT}%`,
  exclusive: `${UAE_EXCLUSIVE_AGENT_PERCENT}%`,
  adminFee: `AED ${UAE_ADMIN_FEE_SALE_AED.toLocaleString('en-US')}`,
  rentalAdminFee: `AED ${UAE_ADMIN_FEE_RENTAL_AED.toLocaleString('en-US')}`,
  serviceFee: `AED ${UAE_SERVICE_FEE_INSTALMENT_AED.toLocaleString('en-US')}`,
  annualServiceFee: `AED ${(UAE_SERVICE_FEE_INSTALMENT_AED * UAE_SERVICE_FEE_INSTALMENTS).toLocaleString('en-US')}`,
});

/** Strict admin-side validation; returns human-readable problems (empty = valid). */
export const validateUaeModelInput = (input: UaeModelInput): string[] => {
  const errors: string[] = [];
  const intIn = (raw: unknown, min: number, max: number) => {
    const n = toFiniteNumber(raw);
    return n !== null && Number.isInteger(n) && n >= min && n <= max;
  };
  const { percentMin, percentMax, capMaxAed, cycleMonthsMin, cycleMonthsMax } = UAE_MODEL_LIMITS;
  if (!intIn(input.agentPercentBeforeCap, percentMin, percentMax))
    errors.push('agentPercentBeforeCap must be a whole number 0–100');
  if (!intIn(input.agentPercentAfterCap, percentMin, percentMax))
    errors.push('agentPercentAfterCap must be a whole number 0–100');
  if (errors.length === 0 && Number(input.agentPercentAfterCap) < Number(input.agentPercentBeforeCap))
    errors.push('agentPercentAfterCap must be ≥ agentPercentBeforeCap');
  if (!intIn(input.capAed, 1, capMaxAed)) errors.push('capAed must be a positive whole number of dirhams');
  if (!intIn(input.cycleMonths, cycleMonthsMin, cycleMonthsMax)) errors.push('cycleMonths must be 1–60');
  if (input.distressClaimsExtended !== undefined && typeof input.distressClaimsExtended !== 'boolean')
    errors.push('distressClaimsExtended must be true or false');
  return errors;
};
